import { useState, useRef } from "react";
import { Building, MapPin, Calendar, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion, useInView } from "framer-motion";
import AfricaMap from "./AfricaMap";

const Projects = () => {
  const [filter, setFilter] = useState("all");
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const projects = [
  { id: 1, title: "Étude de Marché - Secteur AgTech", client: "Ministère de l'Agriculture", type: "market-study", year: "2024", country: "Sénégal", description: "Analyse complète du marché des technologies agricoles avec identification des opportunités de croissance.", tags: ["Étude de marché", "AgTech", "Innovation"] },
  { id: 2, title: "Audit Financier - Programme WASH", client: "ONG Internationale", type: "financial-analysis", year: "2024", country: "Mali", description: "Audit financier et évaluation de la performance d'un programme d'accès à l'eau.", tags: ["Audit", "Finance", "WASH"] },
  { id: 3, title: "Plan Stratégique - Transformation Digitale", client: "Banque Régionale", type: "strategic-consulting", year: "2023", country: "Côte d'Ivoire", description: "Accompagnement dans la définition et mise en œuvre d'un plan de transformation digitale sur 3 ans.", tags: ["Transformation", "Digital", "Finance"] },
  { id: 4, title: "Étude Sectorielle - E-commerce B2B", client: "Confidentiel", type: "market-study", year: "2023", country: "Maroc", description: "Analyse des tendances du e-commerce B2B et stratégies de positionnement.", tags: ["E-commerce", "B2B", "Maroc"] },
  { id: 5, title: "Évaluation de Programme - Éducation", client: "Banque Mondiale", type: "financial-analysis", year: "2023", country: "Burkina Faso", description: "Évaluation à mi-parcours d'un programme éducatif national.", tags: ["Éducation", "Évaluation", "Impact"] },
  { id: 6, title: "Stratégie d'Expansion - Télécom", client: "Opérateur Télécom", type: "strategic-consulting", year: "2024", country: "Sénégal", description: "Stratégie d'expansion vers 3 nouveaux marchés africains.", tags: ["Télécom", "Expansion", "Stratégie"] },
  { id: 7, title: "Étude de Faisabilité - Énergie Solaire", client: "Ministère de l'Énergie", type: "market-study", year: "2024", country: "Niger", description: "Analyse de faisabilité pour un parc solaire de 50MW.", tags: ["Énergie", "Solaire", "Faisabilité"] },
  { id: 8, title: "Diagnostic Organisationnel", client: "Institution Financière", type: "strategic-consulting", year: "2023", country: "Cameroun", description: "Diagnostic complet et recommandations de restructuration organisationnelle.", tags: ["Organisation", "Diagnostic", "Finance"] }];


  const filters = [
  { id: "all", label: "Tous", count: projects.length },
  { id: "market-study", label: "Études de marché", count: projects.filter((p) => p.type === "market-study").length },
  { id: "financial-analysis", label: "Analyses financières", count: projects.filter((p) => p.type === "financial-analysis").length },
  { id: "strategic-consulting", label: "Conseil stratégique", count: projects.filter((p) => p.type === "strategic-consulting").length }];


  const filteredProjects = projects.filter((p) => filter === "all" || p.type === filter);

  const getTypeColor = (type: string) => {
    switch (type) {
      case "market-study":return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800";
      case "financial-analysis":return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800";
      case "strategic-consulting":return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800";
      default:return "bg-muted text-muted-foreground";
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "market-study":return "Étude de marché";
      case "financial-analysis":return "Analyse financière";
      case "strategic-consulting":return "Conseil stratégique";
      default:return type;
    }
  };

  return (
    <section id="projects" className="py-24 bg-background" ref={ref}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}>
          
          <span className="inline-block text-[hsl(var(--brand-red))] text-sm font-semibold tracking-widest uppercase mb-3">Portfolio</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">Projets Réalisés</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Découvrez nos réalisations récentes et les résultats obtenus pour nos clients à travers l'Afrique.
          </p>
        </motion.div>

        {/* Layout: Map + List */}
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 items-start">
          
          {/* Africa Map - Left */}
          <motion.div
            className="lg:col-span-2 lg:sticky lg:top-32"
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}>
            
            <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-[var(--shadow-card)]">
              <AfricaMap />
            </div>
          </motion.div>

          {/* Projects List - Right */}
          <div className="lg:col-span-3 space-y-6">
            {/* Filters */}
            <motion.div
              className="flex flex-wrap gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}>
              
              {filters.map((f) =>
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                filter === f.id ?
                'bg-[hsl(var(--brand-navy))] text-white shadow-md' :
                'bg-muted text-muted-foreground hover:bg-muted/80'}`
                }>
                
                  {f.label}
                  



                
                </button>
              )}
            </motion.div>

            {/* Project List */}
            <div className="space-y-3">
              {filteredProjects.map((project, index) =>
              <motion.div
                key={project.id}
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.07, duration: 0.4 }}
                className="group bg-card border border-border rounded-xl p-4 sm:p-5 hover:border-[hsl(var(--brand-red)/0.3)] hover:shadow-[var(--shadow-premium)] transition-all duration-400 cursor-pointer">
                
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Title row */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-base font-bold text-foreground group-hover:text-[hsl(var(--brand-red))] transition-colors truncate">
                          {project.title}
                        </h3>
                        <Badge variant="outline" className={`text-[10px] shrink-0 ${getTypeColor(project.type)}`}>
                          {getTypeLabel(project.type)}
                        </Badge>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>

                      {/* Meta row */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                        <span className="flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          {project.client}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {project.country}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {project.year}
                        </span>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {project.tags.map((tag, i) =>
                      <span key={i} className="text-[10px] px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{tag}</span>
                      )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-[hsl(var(--brand-red))] group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Summary */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
              className="text-center pt-4">
              
              <p className="text-sm text-muted-foreground">
                Affichage de <span className="font-semibold text-foreground">{filteredProjects.length}</span> projet{filteredProjects.length > 1 ? "s" : ""} sur <span className="font-semibold text-foreground">{projects.length}</span>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>);

};

export default Projects;